import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPersonasData,
  fetchPersonaByIdThunk,
  createPersonaThunk,
  updatePersonaThunk,
  deletePersonaThunk,
  fetchCoreValues,
} from "../thunks/personasThunk.tsx";
import {
  CoreValue,
  PersonaAPIResponseData,
  PersonasState,
} from "../types/personasTypes.tsx";

const initialState: PersonasState = {
  isLoadingList: false,
  isLoadingGenerate: false,
  personas: null,
  coreValues: null,
  isLoadingCoreValues: false,
  isLoadingDetail: false,
  selectedPersona: null,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

export const personasSlice = createSlice({
  name: "personas",
  initialState,
  reducers: {
    clearSelectedPersona: (state) => {
      state.selectedPersona = null;
      state.isLoadingDetail = false;
      state.error = null;
    },
    clearPersonas: (state) => {
      state.personas = initialState.personas;
    },
    clearPersonaById: (state) => {
      state.selectedPersona = initialState.selectedPersona;
    },
    setIsLoadingGenerate: (state, action: PayloadAction<boolean>) => {
      state.isLoadingGenerate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPersonasData.pending, (state) => {
        state.isLoadingList = true;
        state.error = null;
      })
      .addCase(
        fetchPersonasData.fulfilled,
        (state, action: PayloadAction<PersonaAPIResponseData[]>) => {
          state.isLoadingList = false;
          state.personas = action.payload;
        },
      )
      .addCase(fetchPersonasData.rejected, (state, action) => {
        state.isLoadingList = false;
        state.error = (action.payload as string) || action.error.message;
      })

      .addCase(fetchPersonaByIdThunk.pending, (state) => {
        state.isLoadingDetail = true;
        state.selectedPersona = null;
        state.error = null;
      })
      .addCase(
        fetchPersonaByIdThunk.fulfilled,
        (state, action: PayloadAction<PersonaAPIResponseData>) => {
          state.isLoadingDetail = false;
          state.selectedPersona = action.payload;
        },
      )
      .addCase(fetchPersonaByIdThunk.rejected, (state, action) => {
        state.isLoadingDetail = false;
        state.error = (action.payload as string) || action.error.message;
      })

      .addCase(createPersonaThunk.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(
        createPersonaThunk.fulfilled,
        (state, action: PayloadAction<PersonaAPIResponseData>) => {
          state.isCreating = false;
          if (state.personas) {
            state.personas.unshift(action.payload);
          } else {
            state.personas = [action.payload];
          }
        },
      )
      .addCase(createPersonaThunk.rejected, (state, action) => {
        state.isCreating = false;
        state.error =
          (action.payload as string | object) || action.error.message;
      })

      .addCase(updatePersonaThunk.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(
        updatePersonaThunk.fulfilled,
        (state, action: PayloadAction<PersonaAPIResponseData>) => {
          state.isUpdating = false;
          state.selectedPersona = action.payload;
          if (state.personas) {
            const index = state.personas.findIndex(
              (p) => p.id === action.payload.id,
            );
            if (index !== -1) {
              state.personas[index] = action.payload;
            }
          }
        },
      )
      .addCase(updatePersonaThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.error =
          (action.payload as string | object) || action.error.message;
      })

      .addCase(deletePersonaThunk.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(
        deletePersonaThunk.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.isDeleting = false;
          const deletedPersonaId = Number(action.payload);

          if (state.personas) {
            state.personas = state.personas.filter(
              (persona) => persona.id !== deletedPersonaId,
            );
          }

          if (
            state.selectedPersona &&
            state.selectedPersona.id === deletedPersonaId
          ) {
            state.selectedPersona = null;
          }
        },
      )
      .addCase(deletePersonaThunk.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = (action.payload as string) || action.error.message;
      })
      .addCase(fetchCoreValues.pending, (state) => {
        state.isLoadingCoreValues = true;
      })
      .addCase(
        fetchCoreValues.fulfilled,
        (state, action: PayloadAction<CoreValue[]>) => {
          state.isLoadingCoreValues = false;
          state.coreValues = action.payload;
        },
      )
      .addCase(fetchCoreValues.rejected, (state) => {
        state.isLoadingCoreValues = false;
      });
  },
});

export const {
  clearSelectedPersona,
  clearPersonas,
  clearPersonaById,
  setIsLoadingGenerate,
} = personasSlice.actions;
